import axios, { AxiosHeaders, RawAxiosRequestHeaders } from "axios";
import {
  createAddTokenInterceptor,
  createAuthRefreshTokenInterceptor,
} from "../modules/authModule/helpers/AuthRefresh";
import { getAuthValue } from "../modules/authModule/hooks/useAuthValue";
import { CustomAxiosRequestConfig } from "./Types";
import Config from "./EnvConfig";
import { captureSentryApiError } from "./Sentry";
import { appReset } from "../modules/authModule/hooks/useLogoutMutation";

export const axiosInstance = axios.create({
  baseURL: Config.API_BASE_URL,
  timeout: 1000 * 60 * 3, // 3 minutes
  timeoutErrorMessage: "request.timed.out",
});

createAddTokenInterceptor(axiosInstance);
createAuthRefreshTokenInterceptor(axiosInstance);

export const enableApiHeaders = (
  headers?: RawAxiosRequestHeaders | AxiosHeaders,
  options?: {
    includeTenantId?: boolean;
    includeHospitalId?: boolean;
    includeUserId?: boolean;
  }
) => {
  const { includeTenantId = true, includeHospitalId = true, includeUserId = true } = options ?? {};
  const { session_state, realm, userId, hospitalId, tenantId } = getAuthValue();

  const commonHeaders: any = {
    "x-realm": realm,
    "x-session-id": session_state,
    "Content-Type": "application/json",
    "x-app": "ADMIN_APP",
    "x-user-id": userId,
    ...headers,
  };

  if (includeHospitalId) {
    commonHeaders["x-hospital-id"] = headers?.["x-hospital-id"] ?? hospitalId;
  }
  if (includeTenantId) {
    commonHeaders["x-tenant-id"] = headers?.["x-tenant-id"] ?? tenantId;
  }
  if (includeUserId) {
    commonHeaders["x-user-id"] = headers?.["x-user-id"] ?? userId;
  }
  axiosInstance.defaults.headers.common = commonHeaders;
};

export const fetcher = async (config: CustomAxiosRequestConfig) => {
  const { url, method, data, headers, baseURL, authRequired = true } = config;

  if (baseURL) {
    axiosInstance.defaults.baseURL = baseURL;
  }
  if (headers) {
    enableApiHeaders(headers);
  }

  return await axiosInstance.request({
    url,
    method: method ?? "GET",
    data,
    authRequired,
    ...config,
  });
};

export const onError = (error: any) => {
  const { isLoggedIn } = getAuthValue();
  captureSentryApiError(error);
  console.error("API ERROR: ", error);

  if (error?.response?.status === 401 && isLoggedIn) {
    appReset();
  }
};

export const onSuccess = (res: any) => {
  if (res?.status >= 200 && res?.status <= 299) {
    console.log({ success: JSON.stringify(res?.data) });
  } else {
    console.error("error onSucces");
  }
};
